import { Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface PurgeArchivedButtonProps {
  /** Human label of the record, shown in the confirm copy. */
  name: string;
  onPurge: () => void;
  pending?: boolean;
  /** Icon-only (list rows) or a labelled button (detail headers). */
  variant?: 'icon' | 'full';
}

/** Permanently delete an archived CRM record (hard delete). Shows a confirm
 *  dialog; only rendered for archived rows. Backend gates to Founder/Super
 *  Admin and returns 409 if other records still reference it. */
export function PurgeArchivedButton({ name, onPurge, pending, variant = 'icon' }: PurgeArchivedButtonProps) {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {variant === 'full' ? (
          <Button variant="destructive" size="sm" onClick={stop}>
            <Trash2 className="size-4" /> Delete permanently
          </Button>
        ) : (
          <Button
            size="icon"
            variant="ghost"
            aria-label={`Delete ${name} permanently`}
            title="Delete permanently"
            onClick={stop}
          >
            <Trash2 className="text-destructive size-4" />
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent onClick={stop}>
        <AlertDialogHeader>
          <AlertDialogTitle>Permanently delete “{name}”?</AlertDialogTitle>
          <AlertDialogDescription>
            This cannot be undone — the record and its data are removed for good. (If other
            records still reference it, deletion is blocked.)
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={stop}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(e) => {
              stop(e);
              onPurge();
            }}
          >
            Delete permanently
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
